import { setMonth, setYear } from "date-fns"
import { AnimatePresence } from "framer-motion"
import range from "lodash/range"
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react"
import { useTheme } from "styled-components"
import { Input } from "../Input"
import type { LocaleCode } from "../types/common"
import type { WeekStartsOn } from "../types/dates"
import type { BaseProps, BorderProps } from "../types/props"
import { localeFormat } from "../util/dates"
import { ifKeyDown } from "../util/helpers"
import { useClickOutside } from "../util/hooks"
import {
  Curtain,
  CurtainShadow,
  Display,
  Duodecade,
  Month,
  MonthBox,
  MonthInfo,
  Wrapper,
  Year,
  YearBox,
} from "./components"
import {
  CURTAIN_VARIANTS,
  MonthNameFormat,
  getMonthNames,
  resolveMonth,
  tryParseDate,
} from "./util"

type CalendarMode = "month" | "year" | "duodecade"

export interface DatePickerRef {
  clear: (cleared?: boolean) => void
}

export interface DatePickerProps extends BorderProps, BaseProps {
  value: Date

  locale?: LocaleCode
  /**
   * First day of week. Monday is default.
   * @default 1
   */
  weekStartsOn?: WeekStartsOn
  displayDateFormat?: string
  dateErrorText?: string
  dateErrorTimeout?: number
  /** Don't show week numbers */
  noWeekNums?: boolean
  /** Set curtain to be always open */
  keepOpen?: boolean
  /** Whether to close curtain when date is selected. No effect if keepOpen is true */
  closeOnDateChange?: boolean
  /** Disable curtain animation */
  noAnimation?: boolean

  disabled?: boolean
  placeholder?: string
  /** Makes date input clearable. A second argument, cleared, will be sent with onChange if input is cleared. First argument, value, will still be last selected date */
  clearable?: boolean
  /** Whether to init picker as cleared */
  initCleared?: boolean

  width?: string
  fullWidth?: boolean
  /** Height of input */
  height?: string
  background?: string

  selectedBackground?: string

  borderRadius?: string
  focusBorderColor?: string

  onChange: (value: Date, cleared: boolean) => void
  onCleared?: (cleared: boolean) => void
}

export const DatePicker = React.forwardRef<DatePickerRef, DatePickerProps>(
  (
    {
      className,
      id,
      value,

      locale = "enUS",
      weekStartsOn = 1,
      displayDateFormat = "dd/MM/yyyy",
      dateErrorText = "Invalid date",
      dateErrorTimeout = 3000,
      noWeekNums = false,
      keepOpen = false,
      closeOnDateChange = false,
      noAnimation = false,

      disabled = false,
      placeholder,
      clearable = false,
      initCleared = false,

      width = "252px",
      height,
      fullWidth = false,
      background = "white",
      selectedBackground = "primary",

      border,
      borderWidth,
      borderStyle,
      borderColor = "border.default",
      borderRadius = "4px",
      focusBorderColor = "primary",

      onChange,
      onCleared,
    },
    ref,
  ) => {
    const wrapperRef = useRef<HTMLDivElement | null>(null)
    const theme = useTheme()

    const formatDate = useCallback(
      (date: Date, format?: string) =>
        localeFormat(date, format ?? displayDateFormat, locale, weekStartsOn),
      [displayDateFormat, locale, weekStartsOn],
    )

    const [dateInput, setDateInput] = useState(() => formatDate(value))
    const [dateError, setDateError] = useState<null | string>(null)
    const [open, setOpen] = useState(keepOpen || false)
    const [mode, setMode] = useState<CalendarMode>("month")
    const [cleared, setCleared] = useState(clearable && initCleared)

    useImperativeHandle(ref, () => ({
      clear: (cleared = true) => {
        setCleared(cleared)
        if (cleared) {
          setDateInput("")
          onCleared?.(cleared)
        }
      },
    }))

    const toggleCurtain = useCallback(
      (doOpen?: boolean) => {
        const willOpen = keepOpen || (doOpen ?? !open)
        setOpen(willOpen)
      },
      [open, keepOpen],
    )

    useClickOutside(wrapperRef, () => toggleCurtain(false))

    useEffect(() => {
      setDateInput(formatDate(value))
    }, [value, formatDate])

    const emitChange = useCallback(
      (date: Date, cleared = false) => {
        onChange(date, cleared)
        if (cleared) onCleared?.(cleared)

        if (closeOnDateChange && open && !keepOpen) setOpen(false)
      },
      [onChange, open, keepOpen, closeOnDateChange],
    )

    const acceptInput = useCallback(() => {
      if (dateInput === "" && clearable) {
        setCleared(true)
        emitChange(value, true)
        onCleared?.(true)
        return
      }

      const [parsedDate, success] = tryParseDate(dateInput, value)

      if (!success) {
        setDateInput(formatDate(value))
        setDateError(dateErrorText)
        return
      }

      emitChange(parsedDate)
    }, [dateInput, value, dateErrorText, emitChange, formatDate, clearable])

    const handleDateInput = useCallback(
      (value: string) => {
        setDateInput(value)
        if (cleared && value !== "") setCleared(false)
      },
      [cleared],
    )

    const parsedBorder = useMemo(
      () => border ?? theme.layout.defaultBorder,
      [border, theme.layout.defaultBorder],
    )

    const allMonths = useMemo(
      () => getMonthNames(MonthNameFormat.Long, formatDate),
      [formatDate],
    )
    const allYears = useMemo(
      () => range(12).map((i) => value.getFullYear() - 6 + i),
      [value],
    )

    const currentMonth = useMemo(
      () => resolveMonth(value, formatDate, weekStartsOn),
      [value, formatDate, weekStartsOn],
    )

    return (
      <Wrapper
        ref={wrapperRef}
        className={className}
        id={id}
        width={fullWidth ? "100%" : width}
      >
        <Display
          className="--date-picker-display"
          keepOpen={keepOpen}
          focusBorderColor={focusBorderColor}
        >
          <Input
            value={!cleared ? dateInput : ""}
            error={dateError}
            errorTimeout={dateErrorTimeout}
            placeholder={placeholder}
            disabled={disabled}
            iconRightProps={
              keepOpen
                ? undefined
                : {
                    icon: "calendar",
                    boxCursor: disabled ? "default" : "pointer",
                    boxHoverIconColor: disabled ? undefined : "primary",
                    onClick: () => (disabled ? void 0 : toggleCurtain()),
                  }
            }
            fullWidth
            height={height}
            background={background}
            border={parsedBorder}
            borderWidth={borderWidth}
            borderStyle={borderStyle}
            borderColor={borderColor}
            borderRadius={borderRadius}
            focusBorderColor={focusBorderColor}
            onErrorTimeout={() => setDateError(null)}
            onBlur={acceptInput}
            onKeyDown={ifKeyDown("Enter", acceptInput)}
            onChange={handleDateInput}
          />
        </Display>

        <AnimatePresence initial={false} custom={keepOpen}>
          {open && (
            <Curtain
              className="--date-picker-curtain"
              variants={CURTAIN_VARIANTS}
              custom={keepOpen}
              initial={keepOpen ? "visible" : "hidden"}
              animate="visible"
              exit="hidden"
              transition={noAnimation ? { duration: 0 } : undefined}
              background={background}
              border={parsedBorder}
              borderWidth={borderWidth}
              borderStyle={borderStyle}
              borderColor={borderColor}
              keepOpen={keepOpen}
            >
              {mode === "month" && (
                <>
                  <MonthInfo className="--date-picker-curtain-month-info">
                    <span
                      className="--date-picker-curtain-month-info-month"
                      onClick={() => setMode("year")}
                    >
                      {currentMonth.longName}
                    </span>
                    <span
                      className="--date-picker-curtain-month-info-year"
                      onClick={() => setMode("duodecade")}
                    >
                      {currentMonth.year}
                    </span>
                  </MonthInfo>
                  <Month
                    value={value}
                    weekStartsOn={weekStartsOn}
                    noWeekNums={noWeekNums}
                    selectedBackground={selectedBackground}
                    dateFormatter={formatDate}
                    onChange={emitChange}
                  />
                </>
              )}
              {mode === "year" && (
                <Year>
                  {allMonths.map((monthName, idx) => (
                    <MonthBox
                      key={monthName}
                      isCurrent={value.getMonth() === idx}
                      onClick={() => {
                        emitChange(setMonth(value, idx))
                        setMode("month")
                      }}
                    >
                      {monthName}
                    </MonthBox>
                  ))}
                </Year>
              )}
              {mode === "duodecade" && (
                <Duodecade>
                  {allYears.map((yearName) => (
                    <YearBox
                      key={yearName}
                      isCurrent={value.getFullYear() === yearName}
                      onClick={() => {
                        emitChange(setYear(value, yearName))
                        setMode("month")
                      }}
                    >
                      {yearName}
                    </YearBox>
                  ))}
                </Duodecade>
              )}
              {!keepOpen && <CurtainShadow />}
            </Curtain>
          )}
        </AnimatePresence>
      </Wrapper>
    )
  },
)
